<!-- At a Glance. One template for every screen size: the layout is done in CSS so a phone, an
iPad and a desktop all run the same markup and the same bindings. -->
<div class="aag">

  <!-- ── Toolbar ───────────────────────────────────────────────────────────
  Groups that stay together (date, lookup, switches) and wrap as whole
  units rather than breaking apart mid-control. -->
  <header class="aag-bar">

    <div class="aag-bar-group aag-bar-date">
      <button pButton type="button" pTooltip="Previous day" class="aag-icon-btn" (click)="dateChange(-1)">
        <span class="pi pi-chevron-left"></span>
      </button>

      <div class="aag-cal">
        <p-datepicker
          [selectOtherMonths]="true"
          [(ngModel)]="BookingDate"
          firstDayOfWeek="1" dateFormat="dd/mm/yy"
          [showIcon]="true"
          (onSelect)="doneDate()"
          (onChange)="doneDate()"
          [disabledDates]="disabledDatesArr"
          (onMonthChange)="monthchange()">
        </p-datepicker>
      </div>

      <button pButton type="button" pTooltip="Next day" class="aag-icon-btn" (click)="dateChange(1)">
        <span class="pi pi-chevron-right"></span>
      </button>
    </div>

    <!-- Finding a booking by hand, for a till with no scanner. It opens the day's bookings as a
    list that can be filtered and admitted from, on the date the toolbar is showing. -->
    <div class="aag-bar-group aag-bar-lookup">
      <button pButton type="button" pTooltip="Find a booking on this date" class="aag-btn aag-btn--primary" (click)="openBookingList()">
        <span class="pi pi-search"></span><span class="aag-btn-label">Find</span>
      </button>
    </div>

    <!-- TEMPORARY - scanner test, for trying scanning without a scanner. Not shown on a production
    build; remove along with simulateScan() once a real scanner has been tried. -->
    @if (scanTestShown) {
      <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap; padding:6px 10px; border:1px dashed #f59e0b; border-radius:6px; background:#fffbeb;">
        <span style="font-size:13px; font-weight:700; color:#b45309; white-space:nowrap;">Scan Test</span>
        <input pInputText type="text" [(ngModel)]="scanTestValue" (keydown.enter)="simulateScan()" placeholder="" style="height:36px; width:220px;">
        <button pButton type="button" class="p-button p-button-sm p-button-warn" (click)="simulateScan()">
          <span>Scan</span>
        </button>
      </div>
    }

    <!-- The sidebar's own search is out of reach on a phone, so this stands in for it there.
    Hidden by CSS rather than a second template. -->
    <div class="aag-bar-group aag-bar-search aag-compact-only">
      <span class="p-input-icon-left aag-field">
        <i class="pi pi-search"></i>
        <input pInputText type="text" [(ngModel)]="searchCriteria" (keyup.enter)="getSearchResults()"
          placeholder="Search bookings" class="aag-input" />
        </span>
      </div>

      <div class="aag-bar-group aag-bar-switches">
        @if (!inProgress) {
          <p-togglebutton class="aag-toggle"
            pTooltip="Show started sessions"
            onLabel="Hide Started" offLabel="Show Started"
            offIcon="pi pi-stop" onIcon="pi pi-play"
            [(ngModel)]="showDisabled"
            (click)="toggleStarted()">
          </p-togglebutton>
        }

        @if (ShowNewBookingsOnAtAGlance) {
          <button pButton type="button"
            pTooltip="View new customer bookings"
            class="aag-btn aag-btn--amber"
            (click)="viewBookingsToAcknowledge()">
            <span class="pi pi-thumbs-up"></span>
            <span class="aag-btn-label">New Bookings</span>
            <span class="aag-badge">{{BookingsToAcknowledgeCount}}</span>
          </button>
        }
      </div>
    </header>

    <!-- ── Live counters ─────────────────────────────────────────────────────
    Who is in the building right now. Scrolls sideways on a phone instead
    of squashing five tiles into an unreadable row. -->
    @if (UseCounters && ShowCounterStrip) {
      <section class="aag-tiles" aria-label="Live counters">
        <button type="button" class="aag-tile aag-tile--green" (click)="viewCounterBookings('participants')">
          <span class="aag-tile-label">Participants</span>
          <span class="aag-tile-value">{{Counters.participants}}</span>
        </button>
        <button type="button" class="aag-tile aag-tile--amber" (click)="viewCounterBookings('nonparticipants')">
          <span class="aag-tile-label">Non-Participants</span>
          <span class="aag-tile-value">{{Counters.nonparticipants}}</span>
        </button>
        <button type="button" class="aag-tile aag-tile--slate" (click)="viewCounterBookings('timeremaining')">
          <span class="aag-tile-label">Time Remaining</span>
          <span class="aag-tile-value">{{Counters.timeremaining}}</span>
        </button>
        <button type="button" class="aag-tile aag-tile--red" (click)="viewCounterBookings('timeusedup')">
          <span class="aag-tile-label">Time Used Up</span>
          <span class="aag-tile-value">{{Counters.timeusedup}}</span>
        </button>
        <button type="button" class="aag-tile aag-tile--blue" (click)="viewCounterBookings('total')">
          <span class="aag-tile-label">Total</span>
          <span class="aag-tile-value">{{Counters.total}}</span>
        </button>
      </section>
    }

    <!-- ── Day figures ───────────────────────────────────────────────────── -->
    @if (atAGlanceFigures && ShowAtAGlanceFigures) {
      <section class="aag-figures" aria-label="Figures for this day">
        <div class="aag-figure aag-figure--blue">
          <span class="aag-figure-label">Parties</span>
          <span class="aag-figure-value">{{atAGlanceFigures.parties}}</span>
        </div>
        <div class="aag-figure aag-figure--green">
          <span class="aag-figure-label">Party Admissions</span>
          <span class="aag-figure-value">{{atAGlanceFigures.partyadmissions}}</span>
        </div>
        <div class="aag-figure aag-figure--amber">
          <span class="aag-figure-label">Non-Party Admissions</span>
          <span class="aag-figure-value">{{atAGlanceFigures.nonpartyadmissions}}</span>
        </div>
      </section>
    }

    <!-- ── Empty state ───────────────────────────────────────────────────── -->
    @if (!bookingnumbers || bookingnumbers.length === 0) {
      <div class="aag-empty">
        <i class="pi pi-calendar-times"></i>
        <p>There are no bookings for this date</p>
      </div>
    }

    <!-- ── Session deck ──────────────────────────────────────────────────────
    One auto-fitting grid does the whole responsive job: four across on a
    desktop, two on an iPad, one on a phone, with no breakpoints. -->
    @if (bookingnumbers && bookingnumbers.length > 0) {
      <div class="aag-deck">
        @for (bk of bookingnumbersfiltered; track bk) {
          <article class="aag-card" [class.aag-card--live]="bk.started">
            <div class="aag-card-head">
              <div class="aag-time">{{bk.time}}</div>
              @if (bk.started) {
                <span class="aag-pill aag-pill--live">
                  <i class="pi pi-play"></i> In Progress
                </span>
              }
            </div>
            <div class="aag-stats">
              <div class="aag-stat">
                <span class="aag-stat-value">{{bk.bookings}}</span>
                <span class="aag-stat-label">Bookings</span>
              </div>
              <div class="aag-stat">
                <span class="aag-stat-value">{{bk.admissions}}</span>
                <span class="aag-stat-label">People</span>
              </div>
            </div>
            <div class="aag-breakdown">
              <table>
                <thead>
                  <tr>
                    <th>&nbsp;</th>
                    <th class="aag-num">Part.</th>
                    <th class="aag-num">Non-Part.</th>
                  </tr>
                </thead>
                <tbody>
                  @for (bd of bk.breakdown; track bd) {
                    <tr>
                      <td class="aag-bd-name">{{bd.name}}</td>
                      <td class="aag-num">{{bd.participant}}</td>
                      <td class="aag-num">{{bd.nonparticipant}}</td>
                    </tr>
                  }
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td class="aag-num">{{bk.admissionP}}</td>
                    <td class="aag-num">{{bk.admissionNP}}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div class="aag-actions">
              <button pButton type="button" class="aag-btn aag-btn--slate" (click)="bookingblock(bk.time, bk.id, bk.started)">
                <span class="pi pi-eye"></span><span class="aag-btn-label">View</span>
              </button>
              <!-- Nobody in yet: offer to admit them, unless a waiver is outstanding. -->
              @if (UseCounters && (bk.checkedInGuests == 0 && bk.checkedIn == 0)) {
                @if (bk.containswaivers) {
                  <button pButton type="button" class="aag-btn aag-btn--green" disabled="true">
                    <span class="pi pi-sign-in"></span><span class="aag-btn-label">Admit All</span>
                  </button>
                }
                @if (!bk.containswaivers) {
                  <button pButton type="button" class="aag-btn aag-btn--green"
                    [disabled]="!isBookingDateToday" (click)="admitAll(bk.time, bk.id, bk.started)">
                    <span class="pi pi-sign-in"></span><span class="aag-btn-label">Admit All</span>
                  </button>
                }
              }
              <!-- Somebody is already in. -->
              @if (UseCounters && (bk.checkedInGuest > 0 || bk.checkedIn > 0)) {
                <button
                  pButton type="button" class="aag-btn aag-btn--red"
                  [disabled]="!isBookingDateToday" (click)="leaveAll(bk.time, bk.id, bk.started)">
                  <span class="pi pi-sign-out"></span><span class="aag-btn-label">Leave All</span>
                </button>
              }
              <!-- Counters switched off for this venue. -->
              @if (!UseCounters) {
                <button pButton type="button" class="aag-btn aag-btn--green" disabled="true">
                  <span class="pi pi-sign-in"></span><span class="aag-btn-label">Admit All</span>
                </button>
              }
              <button pButton type="button" class="aag-btn aag-btn--blue" (click)="bookSession(bk.time)">
                <span class="pi pi-clock"></span><span class="aag-btn-label">Book</span>
              </button>
              @if (bk.started) {
                <button pButton type="button" class="aag-btn aag-btn--red"
                  (click)="startSession(bk.time, bk.id, bk.started)">
                  <span class="pi pi-stop"></span><span class="aag-btn-label">Stop</span>
                </button>
              }
              @if (!bk.started) {
                <button pButton type="button" class="aag-btn aag-btn--amber"
                  (click)="startSession(bk.time, bk.id, bk.started)">
                  <span class="pi pi-play"></span><span class="aag-btn-label">Start</span>
                </button>
              }
            </div>
          </article>
        }
      </div>
    }
  </div>

  <!-- The content scrolls, so a booking with a lot of answers never pushes the Admit and Leave
  buttons off the screen. -->
  <p-dialog header="Scanned Booking" [(visible)]="showScanDialog" [modal]="true" [style]="{'width': '620px', 'max-width': '95vw'}" [contentStyle]="{'max-height': '65vh', 'overflow-y': 'auto'}" [closable]="true" (onHide)="closeScanDialog()">
    @if (scannedBooking) {
      <div style="padding: 0.25rem 0;">
        <!-- The wrong day changes what every banner under it means - a cancelled ticket for
        next Tuesday is a different conversation from a cancelled one for this morning -
        so it is said before any of them. -->
        @if (scannedBooking.notForToday) {
          <div class="scan-banner scan-banner--nottoday">
            <span class="pi pi-calendar-times"></span>
            <span>
              <b>THIS BOOKING IS NOT FOR TODAY</b>
              <span style="display:block; font-weight:400; margin-top:2px;">It is booked for {{scannedBooking.notForTodayText}}.</span>
            </span>
          </div>
        }
        <!-- A cancelled booking must be the first thing read, and it cannot be admitted. -->
        @if (scannedBookingIsDisabled()) {
          <div class="scan-banner scan-banner--cancelled">
            <span class="pi pi-ban"></span>
            <span>
              <b>This booking has been CANCELLED</b>
              @if (scannedBooking.cancelreason) {
                <span style="display:block; font-weight:400; margin-top:2px;">{{scannedBooking.cancelreason}}</span>
              }
            </span>
          </div>
        }
        <!-- Scanning a ticket that is already in is the thing to catch at the door, so it is
        said before anything else the booking has to tell you. -->
        @if (scannedBookingIsAdmitted()) {
          <div class="scan-banner scan-banner--admitted">
            <span class="pi pi-check-circle"></span>
            <span>This booking has ALREADY BEEN ADMITTED</span>
          </div>
        }
        <!-- An allergy is the one thing nobody can afford to scroll past. -->
        @if (scannedBooking.allergy) {
          <div class="scan-banner scan-banner--allergy">
            <span class="pi pi-exclamation-triangle"></span>
            <span><b>ALLERGY:</b>&nbsp;{{scannedBooking.allergy}}</span>
          </div>
        }
        @if (scannedBooking.notes) {
          <div class="scan-banner scan-banner--notes">
            <span class="pi pi-file"></span>
            <span><b>Notes:</b>&nbsp;{{scannedBooking.notes}}</span>
          </div>
        }
        <!-- Money still owed, with the way to take it right beside the figure. -->
        @if (scannedBooking.outstanding > 0) {
          <div class="scan-balance">
            <div>
              <div class="scan-balance-label">Outstanding balance</div>
              <div class="scan-balance-value">{{scannedBooking.outstanding | currency: (scannedBooking.currency || CurrencySymbol)}}</div>
            </div>
            <button pButton type="button" class="p-button-danger scan-btn" (click)="openScanPayment()"><span class="p-button-icon p-button-icon-left pi pi-credit-card" aria-hidden="true"></span><span class="p-button-label">Pay</span></button>
          </div>
        }
        @if (scannedBooking.outstanding <= 0) {
          <div class="scan-banner scan-banner--paid">
            <span class="pi pi-check-circle"></span>
            <span>Paid in full</span>
          </div>
        }
        <table style="width:100%; border-collapse: collapse; margin-top: 12px;">
          <tr>
            <td style="font-weight:bold; padding: 6px 8px; width:30%;">Booking Ref</td>
            <td style="padding: 6px 8px;">{{scannedBooking.bookingIdHR}}</td>
          </tr>
          <tr style="background-color:#f5f5f5;">
            <td style="font-weight:bold; padding: 6px 8px;">Name</td>
            <td style="padding: 6px 8px;">{{scannedBooking.customer?.fullName != null ? scannedBooking.customer.fullName : ''}}</td>
          </tr>
          <tr>
            <td style="font-weight:bold; padding: 6px 8px;">Email</td>
            <td style="padding: 6px 8px;">{{scannedBooking.customer?.email != null ? scannedBooking.customer.email : ''}}</td>
          </tr>
          <tr style="background-color:#f5f5f5;">
            <td style="font-weight:bold; padding: 6px 8px;">Phone</td>
            <td style="padding: 6px 8px;">{{scannedBooking.customer?.mobile != null ? scannedBooking.customer.mobile : ''}}</td>
          </tr>
          <tr>
            <td style="font-weight:bold; padding: 6px 8px;">Package</td>
            <td style="padding: 6px 8px;">{{scannedBooking.packageName}}</td>
          </tr>
          <tr style="background-color:#f5f5f5;">
            <td style="font-weight:bold; padding: 6px 8px;">Start Time</td>
            <td style="padding: 6px 8px;">{{scannedBooking.startTime | date:'dd/MM/yyyy HH:mm' : 'UTC'}}</td>
          </tr>
          <tr>
            <td style="font-weight:bold; padding: 6px 8px;">Guests</td>
            <td style="padding: 6px 8px;">{{scannedBooking.guests}}</td>
          </tr>
          <tr style="background-color:#f5f5f5;">
            <td style="font-weight:bold; padding: 6px 8px;">Admissions</td>
            <td style="padding: 6px 8px;">{{scannedBooking.admissiontext}}</td>
          </tr>
        </table>
        <!-- The booking's questions and answers, when Show Questions on Scanned Booking is on. -->
        @if (scannedBooking.questionAnswers?.length > 0) {
          <div class="scan-questions">
            <div class="scan-questions-sep"><span>Booking Questions</span></div>
            <table style="width:100%; border-collapse: collapse;">
              @for (qa of scannedBooking.questionAnswers; track qa; let odd = $odd) {
                <tr [style.background-color]="odd ? '#f5f5f5' : ''">
                  <td style="font-weight:bold; padding: 6px 8px; width:40%; vertical-align: top;">{{qa.question}}</td>
                  <td style="padding: 6px 8px; vertical-align: top;">
                    @for (line of qa.lines; track line) {
                      <div>{{line}}</div>
                    }
                  </td>
                </tr>
              }
            </table>
          </div>
        }
      </div>
    }
    <ng-template #footer>
      <button pButton type="button" class="p-button-help scan-btn" (click)="editScannedBooking()" style="margin-right: 10px;"><span class="p-button-icon p-button-icon-left pi pi-pencil" aria-hidden="true"></span><span class="p-button-label">Edit</span></button>
      @if (!scannedBookingIsAdmitted()) {
        <button pButton type="button" class="p-button-success scan-btn" [disabled]="scannedBookingIsDisabled()" (click)="admitScannedBooking()"><span class="p-button-icon p-button-icon-left pi pi-sign-in" aria-hidden="true"></span><span class="p-button-label">Admit</span></button>
      }
      @if (scannedBookingIsAdmitted()) {
        <button pButton type="button" class="p-button-danger scan-btn" (click)="leaveScannedBooking()"><span class="p-button-icon p-button-icon-left pi pi-sign-out" aria-hidden="true"></span><span class="p-button-label">Leave</span></button>
      }
    </ng-template>
  </p-dialog>


  <!-- Taking a payment against the scanned booking. Sits over the scanned-booking dialog rather
  than replacing it, so the balance behind updates once the payment has gone through. -->
  <p-dialog header="Take a Payment" [(visible)]="showScanPayment" [modal]="true" [style]="{'width': '520px'}" [contentStyle]="{'min-height': '380px'}" [closable]="true" (onHide)="closeScanPayment()">
    @if (scannedBooking) {
      <div style="padding: 0.25rem 0;">
        <div class="scan-balance" style="margin-bottom: 16px;">
          <div>
            <div class="scan-balance-label">Outstanding balance</div>
            <div class="scan-balance-value">{{scannedBooking.outstanding | currency: (scannedBooking.currency || CurrencySymbol)}}</div>
          </div>
          <div style="text-align:right;">
            <div class="scan-balance-label">{{scannedBooking.bookingIdHR}}</div>
            <div style="font-weight:600;">{{scannedBooking.customer?.fullName}}</div>
          </div>
        </div>
        <div style="margin-bottom: 16px;">
          <label class="font-medium" style="display:block; margin-bottom:6px;">Amount</label>
          <p-inputnumber [(ngModel)]="paymentAmount" mode="currency" [currency]="scannedBooking.currency || CurrencySymbol"
            [style]="{'width':'100%'}" [inputStyle]="{'width':'100%','height':'56px','font-size':'1.3rem','font-weight':'700'}"
          [ngClass]="{'ng-invalid ng-dirty': !paymentValueValid}"></p-inputnumber>
          @if (!paymentValueValid) {
            <div class="text-danger" style="margin-top:6px;"><span class="pi pi-exclamation-triangle" style="margin-right:5px;"></span>Enter an amount greater than zero.</div>
          }
        </div>
        <div style="margin-bottom: 8px;">
          <label class="font-medium" style="display:block; margin-bottom:6px;">Payment Method</label>
          <p-select [options]="PaymentTypes" [(ngModel)]="selectedPayment" optionLabel="name" placeholder="Choose a payment method" appendTo="body"
          [style]="{'width':'100%'}" [ngClass]="{'ng-invalid ng-dirty': !paymentTypeValid}"></p-select>
          @if (!paymentTypeValid) {
            <div class="text-danger" style="margin-top:6px;"><span class="pi pi-exclamation-triangle" style="margin-right:5px;"></span>Choose a payment method.</div>
          }
        </div>
      </div>
    }
    <ng-template #footer>
      <button pButton type="button" class="p-button-secondary scan-btn" (click)="closeScanPayment()" style="margin-right: 10px;"><span class="p-button-icon p-button-icon-left pi pi-times" aria-hidden="true"></span><span class="p-button-label">Cancel</span></button>
      <button pButton type="button" class="p-button-success scan-btn" [disabled]="paymentButtonLocked" (click)="takeScanPayment()"><span class="p-button-icon p-button-icon-left pi pi-check" aria-hidden="true"></span><span class="p-button-label">Make Payment</span></button>
    </ng-template>
  </p-dialog>

  <!-- The card reader has the amount; nothing else can happen until it is finished with. -->
  <p-dialog [(visible)]="showTerminalWaiting" [modal]="true" [closable]="false" [draggable]="false" [resizable]="false" [style]="{width: '380px'}">
    <div style="display:flex; flex-direction:column; align-items:center; gap:20px; padding:32px 24px; text-align:center;">
      <i class="pi pi-spin pi-spinner" style="font-size:3rem; color:#2563eb;"></i>
      <div style="font-size:1.2rem; font-weight:700; color:#111827;">Waiting for Terminal</div>
      <div style="font-size:0.95rem; color:#6b7280;">Please complete the payment on the card reader.</div>
      <button pButton pRipple type="button"
        class="p-button p-button-secondary scan-btn"
        (click)="cancelTerminalPayment()"><span class="p-button-icon p-button-icon-left pi pi-times" aria-hidden="true"></span><span class="p-button-label">Cancel Payment</span></button>
      </div>
    </p-dialog>

    <!-- A reader part-way through someone else's transaction can be waited for or cleared. -->
    <p-dialog [(visible)]="showTerminalBusyDialog" [modal]="true" [closable]="false" [draggable]="false" [resizable]="false" [style]="{width: '420px'}">
      <div style="display:flex; flex-direction:column; align-items:center; gap:20px; padding:28px 24px 8px; text-align:center;">
        <div style="width:60px; height:60px; border-radius:50%; background:#fff7ed; display:flex; align-items:center; justify-content:center;">
          <i class="pi pi-exclamation-triangle" style="font-size:2rem; color:#d97706;"></i>
        </div>
        <div style="font-size:1.2rem; font-weight:700; color:#111827;">Terminal In Use</div>
        <div style="font-size:0.95rem; color:#6b7280; line-height:1.5;">This terminal is currently processing another transaction. Would you like to wait for it to finish, or clear the terminal and retry?</div>
      </div>
      <ng-template #footer>
        <div style="display:flex; justify-content:center; gap:10px; padding-bottom:8px;">
          <button pButton pRipple type="button"
            class="p-button p-button-secondary p-button-outlined scan-btn"
            (click)="waitForTerminal()"><span class="p-button-icon p-button-icon-left pi pi-clock" aria-hidden="true"></span><span class="p-button-label">Wait</span></button>
            <button pButton pRipple type="button"
              class="p-button p-button-danger scan-btn"
              (click)="clearTerminalAndRetryPayment()"><span class="p-button-icon p-button-icon-left pi pi-replay" aria-hidden="true"></span><span class="p-button-label">Clear Terminal</span></button>
            </div>
          </ng-template>
        </p-dialog>
